import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Card, Col, Form, Row, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import Select from 'react-select';

const WarehouseView = () => {
  const navigate = useNavigate();
  const { pointindex, id } = useParams();
  const [warehouseData, setWarehouseData] = useState({
    branch: null,
    warehousename: "",
    warehousetype: "",
    contactperson: "",
    email: "",
    mobileno: "",
    address: "",
    zipcode: "",
    country: null,
    state: null,
    city: null,
    statecode: ""
  });

  const [branchOptions, setBranchOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [warehouseRes, branchesRes, countriesRes] = await Promise.all([
          axios.get(`${BASE_URL}warehouse/findById/${id}`, { headers: authHeader() }),
          axios.get(`${BASE_URL}branch/findAllBranch`, { headers: authHeader() }),
          axios.get(`${BASE_URL}country/findAllCountry`, { headers: authHeader() })
        ]);

        const warehouse = warehouseRes.data;
        const branches = branchesRes.data;
        const countries = countriesRes.data;

        setBranchOptions(branches.map(b => ({ value: b.id, label: b.branchname })));
        setCountryOptions(countries.map(c => ({ value: c.id, label: c.countryname })));

        let states = [];
        let cities = [];

        if (warehouse.country) {
          const statesRes = await axios.get(
            `${BASE_URL}state/stateByCountry/${warehouse.country.id}`,
            { headers: authHeader() }
          );
          states = statesRes.data;
          setStateOptions(states.map(s => ({
            value: s.id,
            label: s.statename,
            code: s.statecode
          })));
        }

        if (warehouse.state) {
          const citiesRes = await axios.get(
            `${BASE_URL}city/cityByStateId/${warehouse.state.id}`,
            { headers: authHeader() }
          );
          cities = citiesRes.data;
          setCityOptions(cities.map(c => ({ value: c.id, label: c.cityname })));
        }

        setWarehouseData({
          branch: warehouse.branch ? {
            value: warehouse.branch.id,
            label: warehouse.branch.branchname
          } : null,
          warehousename: warehouse.warehousename,
          warehousetype: warehouse.warehousetype,
          contactperson: warehouse.contactperson,
          email: warehouse.email,
          mobileno: warehouse.mobileno,
          address: warehouse.address,
          zipcode: warehouse.zipcode,
          country: warehouse.country ? {
            value: warehouse.country.id,
            label: warehouse.country.countryname
          } : null,
          state: warehouse.state ? {
            value: warehouse.state.id,
            label: warehouse.state.statename,
            code: warehouse.state.statecode
          } : null,
          city: warehouse.city ? {
            value: warehouse.city.id,
            label: warehouse.city.cityname
          } : null,
          statecode: warehouse.statecode || (warehouse.state ? warehouse.state.statecode : "")
        });

      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch warehouse data. Please try again.',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const getItem = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(BASE_URL + "warehouse/delete/" + id, {
          headers: authHeader(),
        })
          .then(response => {
            if (response.data.length === 0) {
              Swal.fire(
                'Warning',
                'Warehouse present in another module. Delete operation has been cancelled.',
                'warning'
              );
            } else {
              navigate(`/warehouseindex/${pointindex}`);
              Swal.fire(
                'Deleted!',
                'Warehouse deleted successfully.',
                'success'
              );
            }
          })
          .catch((error) => {
            Swal.fire(
              'Error',
              error.response.data,
              'error'
            );
          });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-warehouse mr-2"></i>
                Warehouse Details
              </h5>
              <div>
                <button onClick={() => getItem(id)} className="button-70 btn-danger mr-2">
                  <i className="fas fa-trash mr-1"></i> Delete
                </button>
                <Link to={`/warehouseEdit/${id}/${pointindex}`} className="button-70 btn-warning mr-2">
                  <i className="fas fa-edit mr-1"></i> Edit
                </Link>
                <Link to={`/warehouseindex/${pointindex}`} className="button-70 btn-light">
                  <i className="fas fa-arrow-left mr-1"></i> Back
                </Link>
              </div>
            </Card.Header>

            <Card.Body>
              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fas fa-info-circle mr-2"></i>Basic Information
                </h6>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Warehouse Name</Form.Label>
                      <Form.Control plaintext readOnly value={warehouseData.warehousename || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Warehouse Type</Form.Label>
                      <Form.Control plaintext readOnly value={warehouseData.warehousetype || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Branch</Form.Label>
                      <Select
                        value={warehouseData.branch}
                        options={branchOptions}
                        isDisabled
                        placeholder="Not specified"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Contact Person</Form.Label>
                      <Form.Control plaintext readOnly value={warehouseData.contactperson || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Email</Form.Label>
                      <Form.Control plaintext readOnly value={warehouseData.email || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Phone No.</Form.Label>
                      <Form.Control plaintext readOnly value={warehouseData.mobileno || "N/A"} />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fas fa-map-marker-alt mr-2"></i>Address Details
                </h6>
                <Row>
                  <Col md={12} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Address</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        plaintext
                        readOnly
                        value={warehouseData.address || "N/A"}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Country</Form.Label>
                      <Select
                        value={warehouseData.country}
                        options={countryOptions}
                        isDisabled
                        placeholder="Not specified"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">State</Form.Label>
                      <Select
                        value={warehouseData.state}
                        options={stateOptions}
                        isDisabled
                        placeholder="Not specified"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">State Code</Form.Label>
                      <Form.Control plaintext readOnly value={warehouseData.statecode || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">City</Form.Label>
                      <Select
                        value={warehouseData.city}
                        options={cityOptions}
                        isDisabled
                        placeholder="Not specified"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Zip Code</Form.Label>
                      <Form.Control plaintext readOnly value={warehouseData.zipcode || "N/A"} />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Card.Body>

            <Card.Footer className="bg-light">
              <div className="d-flex justify-content-end">
                <Link to={`/warehouseindex/${pointindex}`} className="btn btn-secondary mr-2">
                  <i className="fas fa-arrow-left mr-1"></i> Back to List
                </Link>
                <Link to={`/warehouseEdit/${id}/${pointindex}`} className="btn btn-primary">
                  <i className="fas fa-edit mr-1"></i> Edit Warehouse
                </Link>
              </div>
            </Card.Footer>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default WarehouseView;